import { z } from 'zod'

/**
 * How a form's PDF opens: `FormVersion.layout`.
 *
 * Every report prints its details the same way — section by section, with
 * station tables and photographs. What makes a termite inspection read as a
 * termite inspection and a pest treatment as a pest treatment is the first
 * page: the client's one-sentence summary, then a verdict, a risk scale, the
 * figures at a glance, the conditions found, the recommendation. That opening
 * is declared per form here, in data, so each report is made for its own
 * questions while `ReportDocument.tsx` still knows no form (decisions 54 and
 * 78, ADR 029).
 *
 * Every block names fields by key. A key the version does not have, or a
 * question that was not asked, simply leaves that part out — a layout is a
 * description of the best case, never a requirement.
 */

const key = z.string().min(1).max(120)
const tone = z.enum(['ok', 'alert', 'neutral'])

/** The headline: which answers produce which sentence. First match wins. */
const verdictBlock = z.object({
  kind: z.literal('verdict'),
  field: key,
  outcomes: z
    .array(
      z.object({
        values: z.array(z.string()).min(1),
        title: z.string().min(1),
        detail: z.string().optional(),
        /** Prose from another answer, printed under the title instead of `detail`. */
        detailField: key.optional(),
        tone,
      }),
    )
    .min(1),
  /** Printed beside the verdict: the front of the property, usually. */
  photoField: key.optional(),
})

/** One answer placed on an ordered scale: Moderate · Moderate to high · High · Extreme. */
const scaleBlock = z.object({
  kind: z.literal('scale'),
  field: key,
  label: z.string().min(1),
  levels: z.array(z.string()).min(2).max(7),
  /** The levels that print red. */
  hot: z.array(z.string()).default([]),
  /** The answer that explains the rating, printed under it. */
  noteField: key.optional(),
})

const figure = z.object({
  label: z.string().min(1),
  field: key,
  /**
   * `value` prints the answer; `count` the number of options chosen ("3 found",
   * or `none` when nothing was); `list` the options joined.
   */
  format: z.enum(['value', 'count', 'list']).default('value'),
  /** Answers that print red. A count turns red when anything was chosen. */
  alert: z.array(z.string()).default([]),
  /** What prints under the figure, from another answer or as fixed words. */
  noteField: key.optional(),
  note: z.string().optional(),
  /** For `count`: the words when nothing was chosen. */
  none: z.string().optional(),
})

/** The figures at a glance: four tiles or a three-column grid. */
const figuresBlock = z.object({
  kind: z.literal('figures'),
  title: z.string().optional(),
  style: z.enum(['tiles', 'grid']).default('grid'),
  items: z.array(figure).min(1).max(8),
})

/** Every chosen option of the named questions, as a numbered list. */
const listBlock = z.object({
  kind: z.literal('list'),
  title: z.string().min(1),
  fields: z.array(key).min(1),
  /** Options that never print, such as "None" or "Not applicable". */
  skip: z.array(z.string()).default([]),
  tone: z.enum(['alert', 'navy']).default('alert'),
  /** Printed when nothing was chosen in any of them. */
  empty: z.string().optional(),
})

/** A tinted panel: the recommendation, what the client can do. */
const panelBlock = z.object({
  kind: z.literal('panel'),
  title: z.string().min(1),
  field: key,
  /** `steps` prints each line of the answer (or each option) as a numbered step; `checklist` as boxes. */
  style: z.enum(['steps', 'checklist', 'prose']).default('steps'),
  meta: z.array(z.object({ label: z.string().min(1), field: key })).default([]),
})

/** A heading, an answer in prose, and the photographs taken against it. */
const textBlock = z.object({
  kind: z.literal('text'),
  title: z.string().min(1),
  field: key,
  /** Options chosen in another question, printed as chips under the prose. */
  chipsField: key.optional(),
})

/** One line per station section: how many were checked and how many had activity. */
const stationsBlock = z.object({
  kind: z.literal('stations'),
  title: z.string().default('Stations'),
})

export const layoutBlockSchema = z.discriminatedUnion('kind', [
  verdictBlock,
  scaleBlock,
  figuresBlock,
  listBlock,
  panelBlock,
  textBlock,
  stationsBlock,
])

/**
 * One phrase of the line a job card shows for the last report at an address:
 * "no live termites, risk high, 2 things to fix".
 *
 * - `field` words an answer: through `values` when the answer is listed there
 *   (an empty string leaves it out), otherwise as the answer itself after
 *   `prefix`, lower-cased when `lower` is set. Several options are joined
 *   "a, b and c".
 * - `count` counts the options chosen or the lines written: `one` for one,
 *   `many` with `{n}` for more, `none` (or nothing) for none.
 * - `stations` sums the report's station sections.
 */
const recapPart = z.union([
  z.object({
    field: key,
    values: z.record(z.string(), z.string()).optional(),
    prefix: z.string().optional(),
    lower: z.boolean().optional(),
  }),
  z.object({ count: key, one: z.string().min(1), many: z.string().min(1), none: z.string().optional() }),
  z.object({ stations: z.literal(true) }),
])

export const reportLayoutSchema = z.object({
  /** The eyebrow over the title: "Standard termite inspection · AS 3660.2". */
  eyebrow: z.string().optional(),
  /** The client's one-sentence summary, printed under the letterhead. */
  leadField: key.optional(),
  blocks: z.array(layoutBlockSchema).max(16).default([]),
  /** The job card's one line for this report, the next time somebody is here. */
  recap: z.array(recapPart).max(6).default([]),
})

export type ReportLayout = z.infer<typeof reportLayoutSchema>
/** A layout as written in the seed, before defaults. */
export type ReportLayoutInput = z.input<typeof reportLayoutSchema>
export type ReportRecapPart = z.infer<typeof recapPart>
export type ReportLayoutBlock = z.infer<typeof layoutBlockSchema>
export type ReportLayoutFigure = z.infer<typeof figure>

/**
 * Reads `FormVersion.layout` without trusting it. A malformed layout prints
 * the details alone rather than failing a report: the summary is the
 * flourish, the answers are the record.
 */
export function parseReportLayout(raw: unknown): ReportLayout | null {
  if (raw === null || raw === undefined) return null
  const parsed = reportLayoutSchema.safeParse(raw)
  return parsed.success ? parsed.data : null
}

/** The figures `reportRecap` needs from one station section of a report. */
export type RecapStations = { total: number; checked: number; alert: number }

const joinWords = (words: string[]) =>
  words.length <= 1 ? (words[0] ?? '') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`

function recapPhrase(
  part: ReportRecapPart,
  answers: Record<string, unknown>,
  stations: RecapStations[],
): string | null {
  if ('stations' in part) {
    const total = stations.reduce((sum, section) => sum + section.total, 0)
    if (total === 0) return null
    const alert = stations.reduce((sum, section) => sum + section.alert, 0)
    const checked = stations.reduce((sum, section) => sum + section.checked, 0)
    if (alert > 0) return `${alert} of ${total} stations ${alert === 1 ? 'needs' : 'need'} attention`
    if (checked === total) return total === 1 ? 'the station clear' : `all ${total} stations clear`
    return `${checked} of ${total} stations checked`
  }

  if ('count' in part) {
    const answer = answers[part.count]
    const n = Array.isArray(answer)
      ? answer.length
      : typeof answer === 'string'
        ? answer.split('\n').filter((line) => line.trim() !== '').length
        : 0
    if (n === 0) return part.none ?? null
    return n === 1 ? part.one : part.many.replace('{n}', String(n))
  }

  const answer = answers[part.field]
  const items = (Array.isArray(answer) ? answer : [answer])
    .filter((item) => item !== null && item !== undefined && item !== '')
    .map(String)
  if (items.length === 0) return null

  const words = items
    .map((item) => {
      const mapped = part.values?.[item]
      if (mapped !== undefined) return mapped
      const text = part.lower ? item.toLowerCase() : item
      return items.length === 1 ? `${part.prefix ?? ''}${text}` : text
    })
    .filter((word) => word !== '')
  if (words.length === 0) return null

  const joined = joinWords(words)
  return items.length > 1 && part.prefix ? `${part.prefix}${joined}` : joined
}

/**
 * The line a job card shows for a report the next time somebody is at the
 * address: what the form's `recap` makes of its answers, or null when the
 * form declares none or nothing it names was answered. A summary of the
 * record, never a suggestion: it fills nothing in (decision 55).
 */
export function reportRecap(
  layout: ReportLayout | null,
  answers: Record<string, unknown>,
  stations: RecapStations[] = [],
): string | null {
  if (!layout || layout.recap.length === 0) return null
  const phrases = layout.recap
    .map((part) => recapPhrase(part, answers, stations))
    .filter((phrase): phrase is string => phrase !== null && phrase !== '')
  return phrases.length === 0 ? null : phrases.join(', ')
}
